import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState("");

  useEffect(() => {
    let stopProfile = null;
    let accountVersion = 0;

    const stopAuth = onAuthStateChanged(
      auth,
      (currentUser) => {
        const version = ++accountVersion;

        if (stopProfile) {
          stopProfile();
          stopProfile = null;
        }

        setUser(currentUser);
        setProfile(null);
        setProfileError("");
        setLoading(true);

        if (!currentUser) {
          setLoading(false);
          return;
        }

        stopProfile = onSnapshot(
          doc(db, "users", currentUser.uid),
          (snapshot) => {
            if (version !== accountVersion) return;

            setProfile(snapshot.exists() ? snapshot.data() : null);
            setProfileError("");
            setLoading(false);
          },
          (error) => {
            if (version !== accountVersion) return;

            console.error("Unable to load account profile:", error);
            setProfile(null);
            setProfileError(
              "We could not load your account profile. Please try again."
            );
            setLoading(false);
          }
        );
      },
      (error) => {
        ++accountVersion;

        if (stopProfile) {
          stopProfile();
          stopProfile = null;
        }

        console.error("Unable to check sign-in:", error);
        setUser(null);
        setProfile(null);
        setProfileError(
          "We could not check your sign-in. Please refresh the page."
        );
        setLoading(false);
      }
    );

    return () => {
      ++accountVersion;
      stopAuth();
      if (stopProfile) stopProfile();
    };
  }, []);

  const value = useMemo(
    () => ({
      user,
      profile,
      loading,
      profileError,
      logout: () => signOut(auth),
    }),
    [user, profile, loading, profileError]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider.");
  }

  return context;
}
